/**
 * EstimatedWaitPanel — the rough, per-triage estimated wait for a walk-in.
 *
 * This is the feature the official asdaa.it page doesn't offer, and it is
 * deliberately framed as an ESTIMATE, never a promise (see EstimatedWaitResponse
 * / DECISIONS.md): a standing disclaimer, "circa" wording, a per-row confidence
 * chip, and a `null` wait shown as "non stimabile" rather than a misleading "0".
 *
 * Triage colour is paired with its label and the confidence is given as text,
 * so nothing depends on hue alone (WCAG 2.1 AA).
 */

import type { EstimatedWaitResponse } from "@/api/generated/model";
import { triageMeta } from "@/lib/triage";
import { confidenceLabel, formatWaitMinutes, peopleAheadLabel } from "@/lib/wait";
import { WaitMethodology } from "./WaitMethodology";

interface EstimatedWaitPanelProps {
  data: EstimatedWaitResponse | undefined;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
}

export function EstimatedWaitPanel({ data, isPending, isError, onRetry }: EstimatedWaitPanelProps) {
  return (
    <section className="detail-panel" aria-labelledby="wait-title" aria-busy={isPending}>
      <header className="detail-panel__head">
        <h2 id="wait-title" className="detail-panel__title">
          Attesa stimata
        </h2>
      </header>

      <p className="estimate-disclaimer">
        Stima approssimativa di quanto attenderebbe chi arriva ora, calcolata dalla velocità con cui
        la coda si è svuotata di recente. È un'indicazione, non una garanzia.
      </p>

      {isPending && <ul className="wait-list wait-list--skeleton" aria-hidden="true" />}

      {isError && !isPending && (
        <div className="detail-panel__error" role="alert">
          <p>Impossibile calcolare l'attesa stimata.</p>
          <button type="button" className="detail-panel__retry" onClick={onRetry}>
            Riprova
          </button>
        </div>
      )}

      {data && !isError && !isPending && (
        <>
          {data.estimates.length === 0 ? (
            <p className="detail-panel__empty">
              Nessuna stima disponibile al momento — in attesa di dati sufficienti.
            </p>
          ) : (
            <ul className="wait-list" role="list" aria-label="Attese stimate per codice di triage">
              {data.estimates.map((e) => {
                const meta = triageMeta(e.triageCode);
                const notEstimable = e.estimatedWaitMinutes === null;
                return (
                  <li key={e.triageCode} className="wait-row">
                    <span className="wait-row__triage">
                      <span
                        className="wait-row__swatch"
                        style={{ backgroundColor: meta.colorVar }}
                        aria-hidden="true"
                      />
                      <span className="wait-row__label">{meta.labelIt}</span>
                    </span>
                    <span className="wait-row__people">{peopleAheadLabel(e.peopleAhead)}</span>
                    <span
                      className={`wait-row__value${notEstimable ? " wait-row__value--none" : ""}`}
                    >
                      {formatWaitMinutes(e.estimatedWaitMinutes)}
                    </span>
                    <span className={`confidence-chip confidence-chip--${e.confidence}`}>
                      {confidenceLabel(e.confidence)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <WaitMethodology />
    </section>
  );
}
